import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "@/lib/router-compat";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import OptimizedImage from "@/components/OptimizedImage";
import BlogToc from "@/components/BlogToc";
import { supabase } from "@/integrations/supabase/client";
import { analytics } from "@/lib/analytics";
import DOMPurify from "dompurify";
import "@/styles/blog-content.css";

const slugify = (text: string, index: number) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || `section-${index}`;

/**
 * Injecte des `id` stables sur les titres (h2/h3) directement dans le HTML
 * avant injection, afin qu'ils survivent aux re-renders de dangerouslySetInnerHTML
 * et que le sommaire puisse y accoler un scroll fiable.
 */
const withHeadingIds = (html: string): string => {
  if (typeof window === "undefined") return html;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("h2, h3").forEach((el, i) => {
      if (!el.id) el.id = slugify(el.textContent?.trim() ?? "", i);
      (el as HTMLElement).style.scrollMarginTop = "6rem";
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
};

interface Post {
  id: string;
  title: string;
  slug: string;
  content: string | null;
  excerpt: string | null;
  cover_image: string | null;
  meta_title: string | null;
  meta_description: string | null;
  published_at: string | null;
  created_at: string;
}

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug!)
        .eq("published", true)
        .or(`published_at.is.null,published_at.lte.${new Date().toISOString()}`)
        .single();
      setPost(data as Post | null);
      if (data) analytics.blogPostView(slug!);
      setLoading(false);
    };
    fetch();
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <div className="py-32 text-center">
          <p className="text-muted-foreground font-body">Chargement...</p>
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <SEOHead title="Article introuvable - JIIA Studio" description="Cet article n'existe pas." />
        <div className="py-32 text-center">
          <h1 className="font-heading text-3xl mb-4">Article introuvable</h1>
          <Link to="/blog" className="text-sm font-body uppercase tracking-widest text-accent">← Retour au blog</Link>
        </div>
      </Layout>
    );
  }

  const publishDate = new Date(post.published_at ?? post.created_at);

  const blogPostingLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.meta_description || post.excerpt,
    image: post.cover_image,
    datePublished: publishDate.toISOString(),
    author: { "@type": "Person", name: "Charline Jacob" },
    publisher: { "@type": "Organization", name: "JIIA Studio" },
  };

  // Auto-extract FAQ pairs (h3 followed by p) from content for FAQPage schema
  const extractFaq = (html: string) => {
    if (typeof window === "undefined") return [];
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const faqs: { q: string; a: string }[] = [];
      doc.querySelectorAll("h3").forEach((h3) => {
        const q = h3.textContent?.trim() || "";
        if (!q.endsWith("?")) return;
        const next = h3.nextElementSibling;
        if (next && next.tagName === "P") {
          faqs.push({ q, a: next.textContent?.trim() || "" });
        }
      });
      return faqs;
    } catch {
      return [];
    }
  };

  // IDs des titres injectés dans le HTML avant l'affichage, pour un scroll fiable
  // depuis le sommaire (résiste aux re-renders).
  const sanitizeHtml = withHeadingIds(DOMPurify.sanitize(post.content || ""));

  const faqPairs = extractFaq(post.content || "");
  const jsonLd: Record<string, unknown>[] = [blogPostingLd];
  if (faqPairs.length >= 2) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqPairs.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    });
  }

  return (
    <Layout>
      <SEOHead
        title={post.meta_title || `${post.title} - JIIA Studio`}
        description={post.meta_description || post.excerpt || `Lisez l'article "${post.title}" sur le blog de JIIA Studio.`}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        ogImage={post.cover_image || undefined}
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: post.title, path: `/blog/${post.slug}` },
        ]}
      />

      <article className="py-24 md:py-32">
        <div className="container max-w-3xl">
          <AnimatedSection>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-body uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" /> Blog
            </Link>

            <time className="block text-xs font-body text-muted-foreground mb-4">
              {publishDate.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
            </time>
            <h1 className="font-heading text-4xl md:text-5xl font-light mb-8 leading-tight">{post.title}</h1>
          </AnimatedSection>

          {post.cover_image && (
            <AnimatedSection delay={0.1}>
              <div className="mb-12 overflow-hidden rounded-sm bg-secondary/30">
                <OptimizedImage
                  src={post.cover_image}
                  alt={post.title}
                  width={1600}
                  className="w-full h-auto max-h-[75vh] object-contain"
                  loading="eager"
                />
              </div>
            </AnimatedSection>
          )}

          <AnimatedSection delay={0.2}>
            <div
              ref={contentRef}
              className="blog-content prose prose-lg max-w-none font-body text-foreground
                prose-headings:font-heading prose-headings:font-light
                prose-a:text-accent prose-a:no-underline hover:prose-a:underline
                prose-img:rounded-sm prose-p:text-muted-foreground prose-p:leading-relaxed
                prose-strong:text-foreground"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml }}
            />
          </AnimatedSection>

          <BlogToc contentRef={contentRef} />

          {/* CTA inline */}
          <AnimatedSection delay={0.3}>
            <div className="mt-16 p-8 md:p-12 bg-secondary rounded-sm text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-light mb-3">
                Envie de concrétiser votre projet ?
              </h2>
              <p className="text-muted-foreground font-body mb-6 max-w-lg mx-auto">
                Discutons de vos envies lors d'un premier rendez-vous offert et sans engagement.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary text-primary-foreground text-sm font-body uppercase tracking-widest hover:opacity-90 transition-opacity duration-300 rounded-sm"
              >
                Prendre rendez-vous <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </AnimatedSection>

          {/* CTA secondaire vers les prestations */}
          <AnimatedSection delay={0.4}>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 py-6 border-t border-border">
              <p className="text-sm text-muted-foreground font-body">
                Découvrez comment JIIA Studio peut vous accompagner
              </p>
              <Link
                to="/prestations"
                className="inline-flex items-center gap-2 text-sm font-body uppercase tracking-widest text-foreground hover:text-accent transition-colors"
              >
                Voir les prestations <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </article>
    </Layout>
  );
};

export default BlogPost;
